import { SyncProblems } from '../sync/sync-problems'
import { useUserSpaces } from '../router/authenticated-layout'
import { useSpacesView } from '../db/spaces-queries'
import { changePagePermission } from '../db/page-permissions'
import { queryOptions } from '@w/react-query/db'
import { s } from '@w/schema'
import { useId, useState } from 'react'
import { useMutation, useQuery } from '@w/react-query'
import { Button } from '@w/ui/components/button'
import { Input } from '@w/ui/components/input'
import { IconButton } from '@w/ui/components/icon-button'
import { ChevronLeftIcon, PlusIcon, UsersIcon } from '@w/ui/components/icon'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectPopup,
  SelectItem,
} from '@w/ui/components/select'
import {
  useNavigate,
  useParams,
  useRouterContext,
  useSearch,
} from '../router/flashcards-fe.router-bindings'

export function WorkspaceSettingsPage() {
  const { workspaceId } = useParams({ from: '/$workspaceId/settings' })
  const { section = 'spaces', spaceId = '' } = useSearch({
    from: '/$workspaceId/settings',
  })
  const controlId = useId()
  const ctx = useRouterContext()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<'member' | 'teacher' | 'admin'>('member')
  const [notice, setNotice] = useState('')
  const directory = useQuery(ctx.rpc.workspace.list.queryOptions(), ctx.queryClient)
  const spaces = useQuery(
    {
      queryKey: ['spaces-panel', workspaceId],
      queryFn: () => ctx.rpcClient.page.roots.query({ workspaceId }),
    },
    ctx.queryClient,
  )
  const userSpaces = useUserSpaces()
  const view = useSpacesView(userSpaces, workspaceId, ctx.queryClient)
  const resource = view.data?.resources[0]
  const localTeams = useQuery(resource ? queryOptions(resource.db.query`SELECT id, title,
    archived, deletedAt
    FROM pages WHERE kind<>'personalRoot' AND NOT EXISTS (SELECT 1 FROM pageRelations r WHERE r.toPageId=pages.id AND r.type='child') ORDER BY title,id`, s.object({ id: s.string(), title: s.string(), archived: s.number(), deletedAt: s.number().nullable() })) : {
      queryKey: ['workspace-settings-unavailable', workspaceId],
      queryFn: () => Promise.resolve([]),
      enabled: false,
    }, resource?.queryClient ?? ctx.queryClient)
  const command = useMutation({
    mutationFn: async ({
      action,
      message,
      after,
    }: {
      action: () => Promise<unknown>
      message: string
      after?: () => void
    }) => {
      const result = await action()
      // Leaving has already refreshed the directory through the RPC command
      // barrier. Navigate before trying to refetch this inaccessible workspace.
      if (after) { after(); return { result, message } }
      await spaces.refetch()
      await directory.refetch()
      return { result, message }
    },
    onSuccess: ({ message }) => {
      setNotice(message)
    },
  })
  const members =
    directory.data?.memberships.filter((member) => member.workspaceId === workspaceId) ??
    []
  // The signed-in user, read once: render never calls the auth-validating getter.
  const [self] = useState(() => ctx.auth.getUser())
  const admin = members.some(
    (member) => member.userId === self.id && member.role === 'admin',
  )
  const shared = spaces.data?.pages.flatMap(space => {
    const local = localTeams.data?.find(page => page.id === space.id)
    if (!local || local.archived || local.deletedAt !== null) return []
    return [{ ...space, name: local.title,
    ownerUserId: spaces.data?.grants.find(grant => grant.pageId === space.id && grant.level === 3)?.principalId,
  }]
  }) ?? []
  const soleAdmin = admin && members.filter(member => member.role === 'admin').length === 1
  const selected =
    section === 'spaces' ? shared.find((space) => space.id === spaceId) : undefined
  const workspace = directory.data?.workspaces.find((value) => value.id === workspaceId)
  const memberName = (id: string | undefined) =>
    id === self.id
      ? self.email
      : (members.find((member) => member.userId === id)?.email ?? 'Account unavailable')
  const selectedMember = members.find((member) => member.userId === userId)
  const lastAdmin =
    selectedMember?.role === 'admin' &&
    members.filter((member) => member.role === 'admin').length === 1
  const manageSpace = selected && (selected.ownerUserId === self.id || admin)

  function open(nextSection: typeof section, nextSpaceId?: string) {
    setNotice('')
    setEmail('')
    setUserId('')
    command.reset()
    navigate({
      to: '/$workspaceId/settings',
      params: { workspaceId },
      search: { section: nextSection, spaceId: nextSpaceId },
    })
  }

  function run(action: () => Promise<unknown>, message: string, after?: () => void) {
    setNotice('')
    command.mutate({ action, message, after })
  }

  const memberPicker = (
    <label
      htmlFor={`${controlId}-member`}
      className="flex flex-col gap-2 text-sm font-medium"
    >
      Workspace member
      <Select
        value={userId}
        onValueChange={(value) => {
          setUserId(value ?? '')
          setRole(members.find((member) => member.userId === value)?.role ?? 'member')
          setNotice('')
        }}
      >
        <SelectTrigger id={`${controlId}-member`} data-testid="workspace-member-picker">
          <SelectValue>{userId ? memberName(userId) : 'Select member'}</SelectValue>
        </SelectTrigger>
        <SelectPopup>
          {members.map((member) => (
            <SelectItem key={member.userId} value={member.userId}>
              {memberName(member.userId)} ({member.role})
            </SelectItem>
          ))}
        </SelectPopup>
      </Select>
    </label>
  )

  const invitationForm = (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault()
        run(
          () =>
            ctx.rpcClient.workspace.invite.mutate({
              workspaceId,
              pageId: selected?.id, level: selected ? 2 : undefined,
              email: email.trim(),
            }),
          'Invitation created. The recipient can accept it in their Invitations tab. No email was sent.',
        )
      }}
    >
      <label
        htmlFor={`${controlId}-email`}
        className="flex flex-col gap-2 text-sm font-medium"
      >
        Recipient email
        <Input
          id={`${controlId}-email`}
          type="email"
          required
          placeholder="name@example.com"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value)
          }}
        />
      </label>
      <p className="text-on-surface-secondary text-sm">
        {selected
          ? `They can edit every page in ${selected.name}. Accepting also joins this workspace.`
          : 'Joins this workspace without access to shared pages. Invite to a shared space to share pages.'}{' '}
        They need an existing account; no email is sent.
      </p>
      <Button
        data-testid="workspace-create-invitation"
        className="self-start"
        variant="filled"
        type="submit"
        disabled={!email.trim() || command.isPending}
      >
        Create invitation
      </Button>
    </form>
  )

  return (
    <main className="bg-surface-0 text-on-surface min-h-dvh px-5 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto flex w-full max-w-2xl flex-col gap-8">
        <header className="flex items-start gap-3">
          <IconButton
            aria-label="Back to pages"
            variant="text"
            semanticRole="secondary"
            onClick={() => {
              navigate({ to: '/' })
            }}
          >
            <ChevronLeftIcon />
            Back to pages
          </IconButton>
          <div className="flex min-w-0 flex-col gap-1">
            <p className="text-on-surface-secondary truncate text-sm">
              {workspace?.name ?? 'Workspace'}
            </p>
            <h1 className="text-2xl font-semibold tracking-tight">Workspace settings</h1>
          </div>
        </header>
        <nav
          aria-label="Workspace settings"
          className="border-separator flex flex-wrap gap-1 border-b pb-3"
        >
          {(
            [
              { id: 'spaces', label: 'Shared spaces' },
              { id: 'members', label: 'Members' },
              { id: 'progress', label: 'Learning progress' },
              { id: 'sync-problems', label: 'Sync problems' },
              {
                id: 'invitations',
                label: `Invitations${directory.data?.invites.length ? ` (${directory.data.invites.length})` : ''}`,
              },
            ] as const
          ).map((tab) => (
            <Button
              key={tab.id}
              size="sm"
              variant={section === tab.id ? 'outlined' : 'text'}
              semanticRole="secondary"
              aria-pressed={section === tab.id}
              disabled={command.isPending}
              onClick={() => {
                open(tab.id)
              }}
            >
              {tab.label}
            </Button>
          ))}
        </nav>
        {section === 'sync-problems' && resource ? <SyncProblems resource={resource} /> : null}
        {directory.data && spaces.data ? (
          <>
            {section === 'spaces' && !selected ? (
              <>
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold">Shared spaces</h2>
                  <p className="text-on-surface-secondary text-sm">
                    Each space has its own members. Everyone in it can read and edit all
                    its pages. Your private pages stay private.
                  </p>
                </div>
                {shared.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {shared.map((space) => (
                      <button
                        key={space.id}
                        className="border-separator hover:bg-surface-1 flex items-center gap-3 rounded-xl border p-4 text-left"
                        onClick={() => {
                          open('spaces', space.id)
                        }}
                      >
                        <UsersIcon className="text-on-surface-secondary size-5 shrink-0" />
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {space.name}
                        </span>
                        <span className="text-on-surface-secondary text-sm">Manage</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="bg-surface-1 text-on-surface-secondary rounded-xl p-5 text-sm">
                    No shared spaces yet. Create one for a class, study group, or
                    collaborator.
                  </p>
                )}
                <form
                  className="border-separator flex flex-col gap-4 border-t pt-6"
                  onSubmit={(event) => {
                    event.preventDefault()
                    run(async () => {
                      const result = await ctx.rpcClient.page.createRoot.mutate({
                        workspaceId,
                        name: name.trim(),
                      })
                      setName('')
                      navigate({
                        to: '/$workspaceId/settings',
                        params: { workspaceId },
                        search: { section: 'spaces', spaceId: result.pageId },
                      })
                      return result
                    }, 'Space created. Only you have access until someone accepts an invitation.')
                  }}
                >
                  <label
                    htmlFor={`${controlId}-name`}
                    className="flex flex-col gap-2 text-sm font-medium"
                  >
                    New shared space
                    <Input
                      id={`${controlId}-name`}
                      data-testid="workspace-new-space-name"
                      required
                      placeholder="e.g. Spanish class"
                      value={name}
                      onChange={(event) => {
                        setName(event.target.value)
                      }}
                    />
                  </label>
                  <Button
                    className="self-start"
                    variant="filled"
                    type="submit"
                    disabled={!name.trim() || command.isPending}
                  >
                    <PlusIcon />
                    Create shared space
                  </Button>
                </form>
              </>
            ) : null}
            {section === 'spaces' && selected ? (
              <section key={selected.id} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <Button
                    className="self-start"
                    size="sm"
                    variant="text"
                    onClick={() => {
                      open('spaces')
                    }}
                  >
                    <ChevronLeftIcon />
                    All shared spaces
                  </Button>
                  <h2 className="text-xl font-semibold">{selected.name}</h2>
                  <p className="text-on-surface-secondary text-sm">
                    Members can read, edit, and delete all pages in this space, including
                    subpages. New members also gain access to its retained history.
                  </p>
                  <p className="text-on-surface-secondary text-sm">
                    Owner: {memberName(selected.ownerUserId)}
                  </p>
                </div>
                <h3 className="font-medium">Invite to this space</h3>
                {invitationForm}
                {manageSpace ? (
                  <details className="border-separator border-t pt-6">
                    <summary className="cursor-pointer text-sm font-medium">
                      Manage membership
                    </summary>
                    <div className="flex flex-col gap-4 pt-4">
                      <p className="text-on-surface-secondary text-sm">
                        Choose a workspace member to remove their access. Ownership can
                        only transfer to someone already in this space.
                      </p>
                      {memberPicker}
                      <div className="flex flex-wrap gap-2">
                        <ConfirmAction
                          key={`revoke-${userId}`}
                          label="Remove from space"
                          confirmation={`Remove ${memberName(userId)} from this space?`}
                          disabled={
                            !userId ||
                            userId === selected.ownerUserId ||
                            command.isPending
                          }
                          onConfirm={() => {
                            run(
                              () =>
                                changePagePermission(ctx, {
                                  workspaceId, pageId: selected.id,
                                  type: 'revoke', principalId: userId,
                                }),
                              'Space access removed.',
                            )
                          }}
                        />
                        <ConfirmAction
                          key={`transfer-${userId}`}
                          label="Transfer ownership"
                          confirmation={`Make ${memberName(userId)} the owner?`}
                          disabled={
                            !userId ||
                            userId === selected.ownerUserId ||
                            command.isPending
                          }
                          onConfirm={() => {
                            run(
                              () =>
                                changePagePermission(ctx, {
                                  workspaceId, pageId: selected.id,
                                  type: 'grant', principalId: userId, level: 3,
                                }),
                              'Ownership transferred.',
                            )
                          }}
                        />
                      </div>
                    </div>
                  </details>
                ) : null}
                <ConfirmAction
                  label="Leave space"
                  confirmation="Leave this space? Its pages will no longer be available to you."
                  disabled={command.isPending}
                  onConfirm={() => {
                    run(() => changePagePermission(ctx, {
                      workspaceId, pageId: selected.id, type: 'revoke', principalId: self.id,
                    }), 'You left the space.')
                  }}
                />
              </section>
            ) : null}
            {section === 'members' ? (
              <section className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold">Workspace members</h2>
                  <p className="text-on-surface-secondary text-sm">
                    Workspace membership and page access are separate. Admins manage the
                    workspace; admins and teachers automatically have access to members’
                    learning progress.
                  </p>
                </div>
                <ul className="flex flex-col gap-3">
                  {members.map((member) => (
                    <li
                      key={member.userId}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="min-w-0 truncate">
                        {memberName(member.userId)}
                        {member.userId === self.id ? ' (you)' : ''}
                      </span>
                      <span className="text-on-surface-secondary capitalize">
                        {member.role}
                      </span>
                    </li>
                  ))}
                </ul>
                <h3 className="font-medium">Invite to workspace</h3>
                {invitationForm}
                {admin ? (
                  <details className="border-separator border-t pt-6">
                    <summary className="cursor-pointer text-sm font-medium">
                      Manage roles and membership
                    </summary>
                    <div className="flex flex-col gap-4 pt-4">
                      {memberPicker}
                      {userId ? (
                        <>
                          <label
                            htmlFor={`${controlId}-role`}
                            className="flex flex-col gap-2 text-sm font-medium"
                          >
                            Role
                            <Select
                              value={role}
                              onValueChange={(value) => {
                                if (value) setRole(value)
                              }}
                            >
                              <SelectTrigger id={`${controlId}-role`} data-testid="workspace-member-role">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectPopup>
                                {(['member', 'teacher', 'admin'] as const).map(
                                  (value) => (
                                    <SelectItem key={value} value={value}>
                                      {value}
                                    </SelectItem>
                                  ),
                                )}
                              </SelectPopup>
                            </Select>
                          </label>
                          <Button
                            className="self-start"
                            disabled={
                              command.isPending ||
                              selectedMember?.role === role ||
                              (lastAdmin && role !== 'admin')
                            }
                            onClick={() => {
                              run(
                                () =>
                                  ctx.rpcClient.workspace.setRole.mutate({
                                    workspaceId,
                                    userId,
                                    role,
                                  }),
                                'Role updated.',
                              )
                            }}
                          >
                            Save role
                          </Button>
                          <ConfirmAction
                            label="Remove from workspace"
                            confirmation={`Remove ${memberName(userId)} and their workspace access? `}
                            disabled={command.isPending || lastAdmin}
                            onConfirm={() => {
                              run(
                                () =>
                                  ctx.rpcClient.workspace.removeMember.mutate({
                                    workspaceId,
                                    userId,
                                  }),
                                'Workspace member removed.',
                              )
                            }}
                          />
                          {lastAdmin ? (
                            <p className="text-on-surface-secondary text-sm">
                              This is the last admin. Assign another admin before removing
                              or changing their role.
                            </p>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </details>
                ) : null}
                <div className="border-separator flex flex-col gap-3 border-t pt-6">
                  {soleAdmin ? <p className="text-on-surface-secondary text-sm">Assign another admin before leaving this workspace.</p> : null}
                  <ConfirmAction label="Leave workspace"
                    confirmation="Leave this workspace? Its pages will become unavailable."
                    disabled={command.isPending || soleAdmin}
                    onConfirm={() => { run(() => ctx.rpcClient.workspace.leave.mutate({ workspaceId }), 'You left the workspace.', () => { navigate({ to: '/' }) }) }} />
                </div>
              </section>
            ) : null}
            {section === 'progress' ? (
              <section className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-semibold">Learning progress</h2>
                  <p className="text-on-surface-secondary text-sm">
                    Your cards and review history are private: admins and teachers cannot read them, and only you can change them.
                  </p>
                  <p className="text-on-surface-secondary text-sm">
                    Progress is independent of page permissions: page access never opens another member’s progress.
                  </p>
                </div>
                <div className="border-separator flex flex-col gap-3 border-t pt-6">
                  <h3 className="font-medium">Progress you can open</h3>
                  {/* Progress is private to each learner (flashcards-spaces.spec.md spaces-role-only-progress-settings). */}
                  <Button className="justify-start" variant="text"
                    onClick={() => { navigate({ to: '/$workspaceId/cards', params: { workspaceId }, search: { view: 'activity' } }) }}>
                    My progress
                  </Button>
                </div>
              </section>
            ) : null}
            {section === 'invitations' ? (
              <section className="flex flex-col gap-4">
                <h2 className="text-lg font-semibold">Your invitations</h2>
                <p className="text-on-surface-secondary text-sm">
                  Invitations across your account. Joining a shared space also joins its
                  workspace.
                </p>
                {directory.data?.invites.length ? (
                  directory.data.invites.map((invite) => (
                    <div
                      key={invite.id}
                      className="border-separator flex flex-col gap-3 rounded-xl border p-4"
                    >
                      <p className="text-sm">
                        {invite.pageId
                          ? 'Shared-space invitation'
                          : 'Workspace invitation'}{' '}
                        ·{' '}
                        {directory.data?.workspaces.find(
                          (value) => value.id === invite.workspaceId,
                        )?.name ?? 'New workspace'}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="filled"
                          disabled={command.isPending}
                          onClick={() => {
                            run(
                              () =>
                                ctx.rpcClient.workspace.acceptInvite.mutate({
                                  workspaceId: invite.workspaceId, inviteId: invite.id,
                                }),
                              'Invitation accepted. The workspace is now available in your workspace switcher.',
                            )
                          }}
                        >
                          Accept invitation
                        </Button>
                        <Button
                          variant="text"
                          disabled={command.isPending}
                          onClick={() => {
                            run(
                              () =>
                                ctx.rpcClient.workspace.cancelInvite.mutate({
                                  workspaceId: invite.workspaceId, inviteId: invite.id,
                                }),
                              'Invitation declined.',
                            )
                          }}
                        >
                          Decline
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="bg-surface-1 text-on-surface-secondary rounded-xl p-5 text-sm">
                    You’re all caught up. New invitations will appear here.
                  </p>
                )}
              </section>
            ) : null}
          </>
        ) : directory.error || spaces.error ? null : (
          <p role="status">Loading workspace…</p>
        )}
        {spaces.error || directory.error ? (
          <Button
            className="self-start"
            variant="outlined"
            onClick={() => {
              void Promise.allSettled([directory.refetch(), spaces.refetch()])
            }}
          >
            Retry loading workspace
          </Button>
        ) : null}
        {command.error || spaces.error || directory.error ? (
          <p role="alert" className="text-sm">
            {(command.error ?? spaces.error ?? directory.error)?.message}
          </p>
        ) : null}
        {notice ? (
          <p role="status" className="bg-surface-1 rounded-xl p-4 text-sm">
            {notice}
          </p>
        ) : null}
      </div>
    </main>
  )
}

function ConfirmAction({
  label,
  confirmation,
  disabled,
  onConfirm,
}: {
  label: string
  confirmation: string
  disabled: boolean
  onConfirm: () => void
}) {
  const [confirming, setConfirming] = useState(false)
  return confirming ? (
    <div className="flex flex-col gap-3">
      <p className="text-sm">{confirmation}</p>
      <div className="flex gap-2">
        <Button
          semanticRole="error"
          variant="outlined"
          disabled={disabled}
          onClick={() => {
            onConfirm()
            setConfirming(false)
          }}
        >
          Confirm
        </Button>
        <Button
          variant="text"
          onClick={() => {
            setConfirming(false)
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  ) : (
    <Button
      className="self-start"
      variant="text"
      disabled={disabled}
      onClick={() => {
        setConfirming(true)
      }}
    >
      {label}
    </Button>
  )
}
